<script lang="ts" setup>
import type { Configuration } from '~/types/configurator'

const activePanel = defineModel<string | null>()

const props = defineProps<{
  configuration: Configuration | null
  values: Record<string, Configuration['values'][number]>
  multiselectableAccessories: Record<string, Configuration['values']>
  is3dValid: boolean
  isMetas: boolean
}>()

const userStore = useUser()
const { showPrices } = storeToRefs(userStore)

function calculateAndFormatPrice(
  option: Configuration['accessoriesOptions'][number]
) {
  let price = 0
  if (option.multiselectable) {
    const accessories = option.values.filter((value) =>
      props.multiselectableAccessories[option.partCategoryNid]?.find(
        (accessory) => accessory.partNid === value.accessoryNid
      )
    )
    accessories.forEach((accessory) => {
      if (accessory.isRemovable) {
        price += accessory.price || 0
      }
    })
  } else {
    price += props.values[option.partCategoryNid]?.partPrice || 0
  }

  return configuratorUtils.getFormattedPrice(
    price,
    props.configuration?.currency
  )
}

function isVNP(option: Configuration['accessoriesOptions'][number]) {
  // if first value has accessoryNid not null, it's VNP
  return option.values[0]?.accessoryNid !== null
}

// TODO: handle multiselectable accessories? - would need some attribute to check if option should have 3D model at all (both admin and api would need to be updated)
// update: now "handles" multiselectable accessories - if accessory should not have 3D model it needs to be picked in admin as "no 3D model" i guess (Sam wanted this)
function missingModel(
  option:
    | Configuration['accessoriesOptions'][number]
    | Configuration['metasOptions'][number]
) {
  if (props.isMetas) {
    return false
  }

  if (configuratorUtils.isAccessoryOption(option) && option.multiselectable) {
    // multiselectable accessory
    const selectedValues = props.multiselectableAccessories[
      option.partCategoryNid
    ]?.map((value) =>
      option.values.find((v) => v.accessoryNid === value.partNid)
    )

    return !!selectedValues?.some((value) => value?.isEmpty)
  }

  return (
    !isVNP(option as Configuration['accessoriesOptions'][number]) &&
    props.values[option.partCategoryNid]?.isEmpty
  )
}

/*
Should no longer be needed since we basically ignore surface on product and package all needed surfaces
function missingSurfaceType(
  option:
    | Configuration['accessoriesOptions'][number]
    | Configuration['metasOptions'][number]
) {
  if (props.isMetas) {
    return false
  }

  if (configuratorUtils.isAccessoryOption(option) && option.multiselectable) {
    // multiselectable accessory
    const selectedValues = props.multiselectableAccessories[
      option.partCategoryNid
    ]?.map((value) =>
      option.values.find((v) => v.accessoryNid === value.partNid)
    )

    return !!selectedValues?.some(
      (value) =>
        value?.hasSurfacesOption &&
        (value?.surfaceTypeNid === null ||
          (value?.surfaceTypeNid &&
            !props.surfaceTypes.get(value.surfaceTypeNid)))
    )
  } else {
    // normal accessory
    const selectedValue = (
      option as Configuration['accessoriesOptions'][number]
    ).values.find(
      (value) =>
        value?.accessoryNid === props.values[option.partCategoryNid]?.partNid
    )
    // surface is missing when selected value has hasSurfaceOption true and surfaceTypeNid null or not found in surfaceTypes
    return (
      selectedValue?.hasSurfacesOption &&
      (selectedValue?.surfaceTypeNid === null ||
        (selectedValue?.surfaceTypeNid &&
          !props.surfaceTypes.get(selectedValue.surfaceTypeNid)))
    )
  }
}
*/

function getValueTitle(value: Configuration['values'][number]) {
  // find corresponding accessory to the value
  const { accessory } = configuratorUtils.findAccessory(
    props.configuration?.accessoriesOptions,
    value.partCategoryNid,
    value.partNid
  )
  // if it is accessory with parent, return parent's title
  if (accessory && accessory.parentNid) {
    const { accessory: parent } = configuratorUtils.findAccessory(
      props.configuration?.accessoriesOptions,
      value.partCategoryNid,
      accessory.parentNid
    )
    if (parent) {
      return parent.title
    }
  }
  return value.partTitle
}
</script>

<template>
  <div
    v-for="option in props.isMetas
      ? props.configuration?.metasOptions
      : props.configuration?.accessoriesOptions"
    :key="option.partCategoryNid"
    class="grid grid-cols-[4rem_1fr] border-b-4 border-neutral-200 last:relative last:border-b-0 last:after:absolute last:after:bottom-[-0.25rem] last:after:h-1 last:after:w-full last:after:bg-neutral-200 last:after:content-[''] hover:cursor-pointer hover:bg-neutral-100 2xl:grid-cols-[5rem_1fr]"
    :class="{
      'bg-yellow-50 hover:bg-yellow-100':
        missingModel(option) && props.is3dValid,
      'bg-neutral-200':
        activePanel === METAS_OPTIONS_PANEL + option.partCategoryNid ||
        activePanel === ACCESSORIES_OPTIONS_PANEL + option.partCategoryNid,
    }"
    @click="
      () => {
        if (
          activePanel ===
          (isMetas
            ? METAS_OPTIONS_PANEL + option.partCategoryNid
            : ACCESSORIES_OPTIONS_PANEL + option.partCategoryNid)
        ) {
          activePanel = null
        } else {
          activePanel = isMetas
            ? METAS_OPTIONS_PANEL + option.partCategoryNid
            : ACCESSORIES_OPTIONS_PANEL + option.partCategoryNid
        }
      }
    "
  >
    <UiTooltip
      placement="right"
      :label="option.title"
      :disabled="!activePanel"
      :offset-size="1"
      class="font-pragmatica-ext rounded-none border-[1px] border-neutral-200 px-2 py-2 text-sm font-bold drop-shadow-sm 2xl:text-base"
    >
      <div
        class="row-span-full flex h-full min-h-16 w-16 items-center justify-center 2xl:min-h-20 2xl:w-20"
      >
        <NuxtImg
          v-if="option.icon"
          :src="withHost(option.icon)"
          class="h-5 w-5"
          :class="{
            'opacity-[.5]':
              activePanel !== METAS_OPTIONS_PANEL + option.partCategoryNid &&
              activePanel !==
                ACCESSORIES_OPTIONS_PANEL + option.partCategoryNid &&
              activePanel !== null,
          }"
          alt=""
        />
        <Icon
          v-else
          name="fa6-solid:gear"
          class="h-5 w-5"
          :class="{
            'text-neutral-400':
              activePanel !== METAS_OPTIONS_PANEL + option.partCategoryNid &&
              activePanel !==
                ACCESSORIES_OPTIONS_PANEL + option.partCategoryNid &&
              activePanel !== null,
          }"
        />
      </div>
    </UiTooltip>
    <template v-if="activePanel === null">
      <div>
        <div
          class="grid min-h-16 w-full grid-cols-3 items-center py-2 pl-2 pr-5 2xl:min-h-20 2xl:pl-5"
          :class="{
            '!grid-cols-2': !showPrices || isMetas,
          }"
        >
          <p class="font-pragmatica-ext text-lg font-bold">
            {{ option.title }}
          </p>
          <p
            v-if="showPrices && !isMetas"
            class="font-pragmatica-ext justify-self-center text-sm font-bold 2xl:text-lg"
          >
            {{
              calculateAndFormatPrice(
                option as Configuration['accessoriesOptions'][number]
              )
            }}
          </p>
          <div class="flex items-center gap-4 justify-self-end">
            <p
              v-if="
                configuratorUtils.isAccessoryOption(option) &&
                !option.multiselectable
              "
            >
              {{ props.values[option.partCategoryNid]?.partTitle }}
            </p>
            <div v-else>
              <p
                v-for="value in multiselectableAccessories[
                  option.partCategoryNid
                ] || []"
                :key="value.partNid"
                class="text-right"
              >
                {{ getValueTitle(value) }}
              </p>
            </div>
            <div
              v-if="values[option.partCategoryNid]?.partImageSrc"
              class="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-neutral-200 2xl:h-16 2xl:w-16"
            >
              <NuxtImg
                :src="
                  withHost(
                    values[option.partCategoryNid]?.partImageSrc +
                      IMG_SIZE_100_100_FIT
                  )
                "
                class="h-[90%] w-[90%] object-scale-down mix-blend-darken"
                alt=""
              />
            </div>
            <div
              v-else
              class="font-pragmatica-ext grid h-12 w-12 place-content-center rounded-full bg-black text-xs font-bold text-white 2xl:h-16 2xl:w-16 2xl:text-sm"
            >
              <template v-if="values[option.partCategoryNid]?.partNid">
                <span
                  v-if="
                    (values[option.partCategoryNid]?.partTitle.length || 0) <=
                    MAX_WORD_LENGTH
                  "
                >
                  {{ values[option.partCategoryNid]?.partTitle }}
                </span>
                <SvgSelected v-else class="size-8 stroke-white stroke-[10]" />
              </template>
              <SvgEmpty
                v-else-if="
                  configuratorUtils.isAccessoryOption(option) &&
                  !option.multiselectable
                "
                class="mx-auto size-7 stroke-white stroke-[10]"
              />
              <IconPlus v-else class="size-12" />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="missingModel(option) && props.is3dValid"
        class="col-span-full px-5 text-xs text-yellow-800 md:mt-[-1rem] 2xl:px-7"
      >
        <p>
          {{ $t('configurator.modelMissing') }}
        </p>
        <!--
          Should not be needed
        <p v-if="missingSurfaceType(option)">
          {{
            $t(
              configuratorUtils.isAccessoryOption(option) &&
                !option.multiselectable
                ? 'configurator.surfaceTypeMissing'
                : 'configurator.multiselectableSurfaceTypeMissing'
            )
          }}
        </p>
        -->
      </div>
    </template>
  </div>
</template>
